import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { SessionProvider } from './lib/session.js';
import { FreshProvider } from './lib/fresh.js';
import { reloadOnTakeover } from './lib/sw-reload.js';
import './styles/global.css';
import { applySkin, readSkin } from './lib/skin.js';

// Before the first render, so the app never appears in one palette and lurches
// into another a beat later.
applySkin(readSkin());

/**
 * The PWA is the owner's, not the visitor's.
 *
 * index.html is the single entry for every route, so anything injected into it
 * also loads on /shared/:token — the one page opened by someone who is not the
 * owner. Registering there precached the whole owner-only bundle onto a
 * clinician's device and offered them a mental-health app to install whose
 * start_url is somebody else's sign-in screen. Worse, a service worker outlives
 * the link: revoking the share does not unregister it.
 *
 * So registration and the install manifest are both gated on the path here,
 * with `injectRegister: false` in vite.config.ts stopping the automatic version.
 */
const isSharedPage = window.location.pathname.startsWith('/shared/');

if (isSharedPage) {
  document.querySelector('link[rel="manifest"]')?.remove();
} else if ('serviceWorker' in navigator) {
  // When a new worker takes over, the page in front of the user is still the
  // old one. Reload once so it is not — without this, an update lands silently
  // and the user goes on looking at the previous build, which is exactly how
  // three deployed fixes failed to reach anybody. Guarded ACROSS loads so it
  // cannot loop (lib/sw-reload.ts; it did, 22 Oct 2026).
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    /* private modes can refuse storage; the guard copes */
  }
  reloadOnTakeover({ sw: navigator.serviceWorker, storage, reload: () => window.location.reload() });

  window.addEventListener('load', () => {
    // generateSW emits /sw.js, and registerType: 'autoUpdate' builds
    // skipWaiting into it, so there is no update prompt to wire up here.
    void navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        // Browsers only check for a new worker on their own schedule. Asking on
        // every boot costs one conditional request and means a fix deployed a
        // minute ago is live on the next open.
        void reg.update().catch(() => {});

        /*
         * And again whenever the app comes back to the front.
         *
         * `load` fires once. An installed PWA is RESUMED far more often than it
         * is launched — iOS keeps the page alive in the background and brings it
         * back untouched — so on a phone that check could go days without
         * running, and the app went on serving a build from whenever it was
         * last cold-started. There is no address bar in standalone mode, so
         * there was no reload button to fall back on either; the only way out
         * was Settings → App → clear the local copy, which is a strange thing
         * to ask of somebody who just wants the current version.
         *
         * Throttled to once a minute so a person flicking between apps does not
         * fire a request each time.
         */
        let lastChecked = Date.now();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return;
          if (Date.now() - lastChecked < 60_000) return;
          lastChecked = Date.now();
          void reg.update().catch(() => {});
        });
      })
      .catch(() => {
        // Offline support is a bonus; the app works without it. Never let a
        // failed registration take the page down with it.
      });
  });
}

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <SessionProvider>
        {/* The clock and the "came back to the app" signal — lib/fresh.tsx. */}
        <FreshProvider>
          <App />
        </FreshProvider>
      </SessionProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
